import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

import { red, green, grey } from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, isNil, isNull, isUndefined } from "lodash";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";

import resourceService from "@plannel/services/data/resource-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import resourceProductionPlanService from "@plannel/services/mp/resource-production-plan-service";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PaginationContainer from "@plannel/components/PaginationContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import Snackbar from "@plannel/components/Snackbar";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";

const { PRODUCTION_PLAN } = CodeUtil.mpMeasure;
const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode, module } = CodeUtil;
const { MP } = module;
const { RELEASED, PLANNED } = versionStatusCode;

const LOCATION_NM_IGNORE_COL_ID = `locNmGrp${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_NM_IGNORE_COL_ID = `itemNmGrp${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_NM_IGNORE_COL_ID = `resNmGrp${GridUtils.IGNORE_P13N_COLLID}`;
const FrozenCellRenderer = ({ value, valueFormatted, label }) => (
  <Tooltip title={label} placement="bottom-start">
    <Box component="span" sx={{ display: "block", width: "100%" }}>{valueFormatted ?? value}</Box>
  </Tooltip>
);

const ResourceProductionPlan = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [10, 30, 50];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});
  const [resourceProdPlan, setResourceProdPlan] = useState([]);

  const gridRef = useRef();
  const bucketColumnDefs = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);
  const locationColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "locationColsHide") ? reduxViewState.locationColsHide : true;
  const resourceColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "resourceColsHide") ? reduxViewState.resourceColsHide : true;
  const itemColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "itemColsHide") ? reduxViewState.itemColsHide : true;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(() => [PRODUCTION_PLAN], []);
  const rowsPerCombination = useMemo(() => { return measures?.length }, [measures]);

  const gridHeaderMap = useRef({});

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const [resourceSearch, setResourceSearch] = useState(reduxViewState?.resourceSearch ?? null);
  const [resource, setResource] = useState([]);

  const [versionList, setVersionList] = useState([]);
  const [selectVersion, setSelectVersion] = useState(reduxViewState?.version ?? null);

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const sortMpVersions = versions => {
    const basicVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        basicVersions.push(ver);
      }
    }

    return [...basicVersions, ...releaseVersions];
  };

  const retrieveVersion = receivedVersion => {
    const params = { moduleCd: MP, statusCds: [PLANNED, RELEASED], orderByColumn: "updatedTs", sortType: "desc" };
    planVersionService.getAll(params)
      .then(response => {
        const { results } = response.data;
        if (results) {
          const sortedVersions = sortMpVersions(results);
          setVersionList(sortedVersions);

          if (receivedVersion) {
            const currentVersion = results.find(result => isEqual(result.id, receivedVersion.id));
            currentVersion ? setSelectVersion(currentVersion) : setSelectVersion(results[0]);
          } else {
            if (reduxViewState?.version) {
              const reduxVersion = results.find(result => isEqual(result.id, reduxViewState.version.id));
              if (isUndefined(reduxVersion)) {
                setSelectVersion(sortedVersions[0]);
              }
            } else {
              setSelectVersion(sortedVersions[0]);
            }
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveResource = () => {
    resourceService.getAll()
      .then(response => {
        const { results } = response.data;
        setResource(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const emptyValueFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value)) {
      return "";
    }
    return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const onCellGridClicked = params => {
    const { locNm, itemCd, itemNm } = params.data;
    const leafChildrenRow = [];
    params.api.forEachLeafNode(node => leafChildrenRow.push(node));
    for (const row of leafChildrenRow) {
      isEqual(locNm, row.data.locNm)
      if (isEqual(locNm, row.data.locNm) && isEqual(itemCd, row.data.itemCd) && isEqual(itemNm, row.data.itemNm)) {
        row.setSelected(true);
      } else {
        row.setSelected(false);
      }
    }
  }

  const defaultColumnDefs = [
    {
      headerName: 'locCd', colId: "locCdGrp", width: 140, showRowGroup: 'locCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: locationColsHide, reduxKey: "locationColsHide",
        detailColumns: [LOCATION_NM_IGNORE_COL_ID]
      }
    },
    {
      headerName: 'locName', width: 140, showRowGroup: 'locNm', pinned: "left", lockPinned: true,
      colId: LOCATION_NM_IGNORE_COL_ID,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', hide: locationColsHide,
      cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'wcCd', colId: "workCenterCdGrp", width: 140, showRowGroup: 'workCenterCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'resCd', colId: "resCdGrp", width: 140, showRowGroup: 'resCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: resourceColsHide, reduxKey: "resourceColsHide",
        detailColumns: [RESOURCE_NM_IGNORE_COL_ID]
      }
    },
    {
      headerName: 'resName', width: 140, showRowGroup: 'resNm', pinned: "left", lockPinned: true,
      colId : RESOURCE_NM_IGNORE_COL_ID,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', hide: resourceColsHide,
      cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'itemCd', colId: "itemCdGrp", showRowGroup: 'itemCd',  width: 140, pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: itemColsHide,reduxKey: "itemColsHide",
        detailColumns: [ITEM_NM_IGNORE_COL_ID]
      }
    },
    {
      headerName: 'itemName', width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
      colId: ITEM_NM_IGNORE_COL_ID,
      type: ["nonEditableColumn"], hide: itemColsHide,
    },
    { headerName: "locCd", field: "locCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "locName", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "workCenterCd", field: "workCenterCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "resCd", field: "resCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "resNm", field: "resNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemType", field: "itemType", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "category", field: "categoryNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
  ];

  const defaultExportColumnDefs = [
    { field: "locCd", colId: "locCdGrp", rowGroup: true, hide: true },
    { field: "locName", colId: "locNameGrp", rowGroup: true, hide: true },
    { field: "wcCd", colId: "workCenterCdGrp", rowGroup: true, hide: true },
    { field: "resCd", colId: "resCdGrp", rowGroup: true, hide: true },
    { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true },
    { field: "itemName", colId: "itemNmGrp", rowGroup: true, hide: true },
    { headerName: "locCd", field: "locCd", width: 140, cellClass: 'stringType' },
    { headerName: "locName", field: "locNm", width: 140 },
    { headerName: "workcenter", field: "workCenterCd", width: 140, cellClass: 'stringType' },
    { headerName: "resCd", field: "resCd", width: 140, cellClass: 'stringType' },
    { headerName: "resName", field: "resNm", width: 140 },
    { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
    { headerName: "itemName", field: "itemNm", width: 140 },
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (selectVersion) {
      params.versionId = selectVersion.id;
      const timeBuckets = selectVersion?.planConfigObj?.TIME_BUCKETS;
      params.timeBuckets = !isEmpty(timeBuckets) ? JSON.stringify(timeBuckets) : null;
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (resourceSearch) {
      params.resCd = resourceSearch.resCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }

      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }

    return { ...params, ...sortParams };
  }

  const isFrozenCell = params => {
    const { data, colDef } = params;
    return !isNil(data?.frozenEndDate) && colDef?.field <= data.frozenEndDate;
  }

  const setGridHeaders = (headers, headerMap) => {
    /* 이전 Column의 Status를 기억하기 때문에 ColumnDefs의 순서를 따라가지 않음에 따라 UK 설정 */
    const bucketPostFix = selectVersion?.id ? GridUtils.IGNORE_P13N_COLLID + selectVersion.id : GridUtils.IGNORE_P13N_COLLID;
    bucketColumnDefs.current =  [];

    headers.forEach((header) => {
      const displayHeader = headerMap[header];
      bucketColumnDefs.current.push({
        headerName: displayHeader, field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        valueFormatter: emptyValueFormatter,
        cellStyle: params => isFrozenCell(params) ? { backgroundColor: grey[300] } : null,
        cellClassRules: { greyBackground: isFrozenCell },
        cellRendererSelector: params => isFrozenCell(params)
          ? { component: FrozenCellRenderer, params: { label: GridUtils.gridValueL10N("frozenRangeSim") } }
          : undefined
      });
    });

    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...defaultColumnDefs, ...bucketColumnDefs.current] });
  }

  const setGridDatas = (headers, results) => {
    const datas = [];
    const productionPlanPivotData = "productionPlanPivotData";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;

      for (const measureName of measure) {
        const data = { ...resultsData, measure: measureName };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {

            case PRODUCTION_PLAN:
              data[header] = resultsData[productionPlanPivotData][idx];
              break;

            default:
              break;
          }
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const retrieveResourceProdPlan = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    resourceProductionPlanService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;
        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          setGridHeaders(localDateHeader, headerMap);
          setResourceProdPlan(setGridDatas(localDateHeader, data));
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
          setResourceProdPlan([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, filter: filterValue, resourceSearch, version: selectVersion, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const onGridReady = params => {
    retrieveVersion();
    retrieveResource();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  }

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveResourceProdPlan();
    }
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iconColor = planVersion => {
    if (planVersion) {
      const { statusCd } = planVersion;
      if (isEqual(statusCd, RELEASED)) {
        return grey[900];
      } else if (isEqual(statusCd, PLANNED)) {
        return green[600];
      }

      return red[600];
    }

    return null;
  }

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
    }

    return null;
  }

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: defaultColumnDefs });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridLoading && selectVersion && !isEmpty(measures)) {
      retrieveResourceProdPlan();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, measures, sortParams]);

  useEffect(() => {
    if (gridLoading && selectVersion && !isEmpty(measures)) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, selectVersion, resourceSearch]);

  return (
    <>
      <FilterContainer>
        <VersionAutocomplete
          options={versionList}
          value={selectVersion}
          onChange={(_, newValue) => setSelectVersion(newValue)}
          label={t("version")}
          getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
          getIconColor={iconColor}
          getTooltip={setToolTip}
          minWidth={250}
          sx={{ mr: 0, pr: 1 }}
          inputIconSx={{ ml: 1 }}
        />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <Autocomplete
          options={resource}
          getOptionLabel={option => option.name}
          value={resourceSearch}
          onChange={(_, newValue) => setResourceSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.resCd === value.resCd}
          size="small"
          sx={{ pr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("resource")} />
          )}
        />
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={resourceProductionPlanService} setExcelData={setGridDatas}  columnDefs={[...defaultExportColumnDefs, ...bucketColumnDefs.current]}
            totalRows={resultTotalCount.current * rowsPerCombination} params={{ ...getRequestParams(false), measureCount: rowsPerCombination }} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area non-background-selected-row" mb={1}>
        <AgGridReact
          ref={gridRef}
          rowData={resourceProdPlan}
          {...defaultGridMemo}
          onGridReady={onGridReady}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
          onCellClicked={onCellGridClicked}
          statusBar={statusBar}
          groupDisplayType={'custom'}
          groupDefaultExpanded={12}
          groupHideOpenParents
        >
        </AgGridReact>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
      />
    </>
  );
};

export default withTranslation()(ResourceProductionPlan);
